import React, {useEffect, useRef} from 'react';
import {Link} from 'react-router-dom';
import './Pillars.css';
import beratungImg from '../assets/images/pillar-beratung.webp';
import wundeImg from '../assets/images/pillar-wunde.webp';
import akademieImg from '../assets/images/pillar-akademie.webp';

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

// Die drei Schwerpunkte von Cura Care. Fakten stammen von den jeweiligen Leistungsseiten.
const pillars = [
  {
    id: 'pflegeberatung',
    index: '01',
    title: 'Pflegeberatungsstelle',
    subtitle: 'Vom ersten Antrag bis zum Widerspruch',
    text:
      'Wir beraten individuell zu Pflege, Versorgung, Anträgen und Entlastungsangeboten und begleiten Sie durch jeden Schritt mit der Pflegekasse.',
    facts: [
      'Beratungsbesuche nach § 37 Abs. 3 SGB XI',
      'Hilfe bei Pflegegrad und Höherstufung',
      'Pflegekurse für Angehörige'
    ],
    to: '/leistungen/beratung',
    image: beratungImg,
    imageAlt: 'Zwei ältere Menschen halten sich an der Hand'
  },
  {
    id: 'wundpflegezentrum',
    index: '02',
    title: 'Wundpflegezentrum',
    subtitle: 'Damit chronische Wunden heilen',
    text:
      'Moderne Versorgung akuter und chronischer Wunden durch qualifizierte Pflegefachkräfte, sorgfältig dokumentiert und mit Ihren Ärztinnen und Ärzten abgestimmt.',
    facts: [
      'Zertifizierte Wundexpertin im Team',
      'Fotodokumentation und Verlaufskontrolle',
      'Schmerz- und Exsudatmanagement'
    ],
    to: '/leistungen/wundmanagement',
    image: wundeImg,
    imageAlt: 'Dienstwagen von Cura Care mit der Aufschrift Wundpflegezentrum'
  },
  {
    id: 'pflegeakademie',
    index: '03',
    title: 'Pflegeakademie',
    subtitle: 'Qualifizierung zum Alltagsbegleiter',
    text:
      'Praxisnahe Weiterbildung nach § 45a SGB XI mit erfahrenen Dozentinnen und Dozenten aus der Pflege, persönlicher Begleitung und Zertifikat.',
    facts: [
      'Basiskurs mit 40 Stunden',
      'Jährlicher Refresher mit 16 Stunden',
      'Zertifikat nach AnFöVO NRW'
    ],
    to: '/leistungen/pflegeakademie',
    image: akademieImg,
    imageAlt: 'Teilnehmende eines Kurses der Cura Care Pflegeakademie im Seminarraum'
  }
];

const Pillars = () => {
  const listRef = useRef(null);

  // Karten blenden beim ersten Sichtbarwerden ein.
  useEffect(() => {
    const cards = listRef.current ? [...listRef.current.children] : [];
    if (!cards.length) return undefined;
    if (!('IntersectionObserver' in window)) {
      cards.forEach((card) => card.classList.add('is-visible'));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      {threshold: 0.2}
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="pillars" id="ueber-uns" aria-labelledby="pillars-title">
      <div className="pillars__inner">
        <div className="pillars__head">
          <div>
            <p className="kicker">Über Cura Care</p>
            <h2 id="pillars-title" className="pillars__title">
              Mehr als ein Pflegedienst: drei Schwerpunkte unter einem Dach.
            </h2>
          </div>
          <p className="pillars__intro">
            Neben der ambulanten Pflege in Eitorf bündelt Cura Care eine Pflegeberatungsstelle,
            ein Wundpflegezentrum und eine eigene Pflegeakademie. Fachlich, verlässlich und mit
            persönlicher Nähe.
          </p>
        </div>

        <div className="pillars__stack" ref={listRef}>
          {pillars.map(({id, index, title, subtitle, text, facts, to, image, imageAlt}, i) => (
            <article className="pillar" key={id} style={{'--i': i}} aria-labelledby={`pillar-${id}`}>
              <figure className="pillar__media">
                <img src={image} alt={imageAlt} width="800" height="600" loading="lazy" />
              </figure>
              <div className="pillar__body">
                <span className="pillar__index" aria-hidden="true">{index}</span>
                <h3 id={`pillar-${id}`}>{title}</h3>
                <p className="pillar__subtitle">{subtitle}</p>
                <p className="pillar__text">{text}</p>
                <ul className="pillar__facts">
                  {facts.map((fact) => (
                    <li key={fact}>
                      <Check />
                      <span>{fact}</span>
                    </li>
                  ))}
                </ul>
                <Link className="pillar__link" to={to} aria-label={`Mehr zur ${title}`}>
                  Mehr dazu <span aria-hidden="true">→</span>
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pillars;
